/* Connect the existing Easify livery choice to the Senna gallery. Never rewrite option values, IDs, prices or cart payloads. */
(() => {
  'use strict';
  const LABELS = {greatness:'Greatness', champion:'Champion', legacy:'Legacy'};
  const HANDLES = new Set(['p2-senna-edition-simulator','p1-s-4dof-senna-edition-simulator','p1-precision-senna-edition-simulator','p1-ddx-senna-limited-edition-simulator']);
  const controllers = new Map();
  const liveryFrom = text => {
    const match = String(text || '').trim().match(/^(greatness|champion|legacy)(?:\s+livery)?(?:\s*[-–—]?\s*\([^)]*\))?\s*$/i);
    return match ? match[1].toLowerCase() : null;
  };
  function start(gallery) {
    if (controllers.has(gallery) || !HANDLES.has(gallery.dataset.productHandle)) return;
    const scope = gallery.closest('.shopify-section') || gallery.closest('[id^="shopify-section-"]');
    if (!scope) return;
    const state = {selected:null, queued:false, force:false, disposed:false, ready:false};
    const controlLiveries = new WeakMap();
    const imageFor = key => gallery.querySelector(`slider-element [data-senna-livery="${key}"][data-senna-view="three-quarter"] img`);
    function syncSticky(key) {
      const image = imageFor(key);
      if (!image) return;
      scope.querySelectorAll('product-sticky-form[data-senna-livery-sticky]').forEach(form => {
        if (form.dataset.productHandle !== gallery.dataset.productHandle) return;
        form.dataset.sennaLivery = key;
        const target = form.querySelector('[data-senna-livery-sticky-image]');
        if (!target) return;
        for (const attribute of ['src','srcset','alt','width','height']) {
          const value = image.getAttribute(attribute);
          if (value !== null && target.getAttribute(attribute) !== value) target.setAttribute(attribute,value);
        }
        target.setAttribute('sizes','80px');
      });
    }
    function showLivery(key, force = false) {
      if (!LABELS[key] || state.disposed || !state.ready) return;
      if (!force && key === state.selected) return;
      const slider = gallery.querySelector('slider-element');
      // Native sliders initialize only when near the viewport. Do not cache an unshown choice.
      if (!slider || !Number.isFinite(slider.currentPage) || !(slider.itemOffset > 0)) return;
      const button = gallery.querySelector(`media-dots button[data-senna-livery="${key}"][data-senna-view="three-quarter"]`);
      if (!button) return;
      const dots = button.closest('media-dots');
      if (typeof dots.init === 'function') dots.init();
      if (typeof dots.onButtonClick === 'function') {
        dots.onButtonClick({currentTarget:button, preventDefault(){}});
      } else {
        button.click();
      }
      state.selected = key;
      gallery.dataset.sennaSelectedLivery = key;
      syncSticky(key);
      gallery.dispatchEvent(new CustomEvent('senna:livery-displayed',{bubbles:true,detail:{livery:key}}));
    }
    function readOptions(force = false) {
      if (state.disposed) return;
      let chosen = null;
      // These stable Easify class names are verified on all four live product pages.
      scope.querySelectorAll('.po-field-root').forEach(field => {
        const heading = field.querySelector('.po-label-label');
        if (!heading || !/\bsenna\s+livery\s+options\b/i.test(heading.textContent)) return;
        field.setAttribute('data-senna-livery-field','');
        field.querySelectorAll('.po-radioGroup-item').forEach(item => {
          const input = item.querySelector('input[type="checkbox"],input[type="radio"]');
          const label = item.querySelector('.po-optionItemLabel-label');
          if (!input || !label) return;
          const key = liveryFrom(label.textContent) || controlLiveries.get(input);
          if (!key) return;
          controlLiveries.set(input,key);
          const cleanLabel = `${LABELS[key]} Livery`;
          if (label.textContent !== cleanLabel) label.textContent = cleanLabel;
          // Preserve label associations and all Easify state. Only accessible display names change.
          if (input.getAttribute('aria-label') && input.getAttribute('aria-label') !== cleanLabel) input.setAttribute('aria-label',cleanLabel);
          for(const node of [item,label]) {
            for(const attribute of ['title','aria-label']) {
              if(node.hasAttribute(attribute) && liveryFrom(node.getAttribute(attribute)) && node.getAttribute(attribute)!==cleanLabel) node.setAttribute(attribute,cleanLabel);
            }
          }
          if(input.checked) chosen = key;
        });
      });
      if(chosen) showLivery(chosen,force);
      else state.selected=null;
      // No auto-selection: keep Easify's required-option validation and existing default state.
    }
    function scheduleRead(force = false) {
      state.force = state.force || force;
      if(state.queued || state.disposed) return;
      state.queued=true;
      requestAnimationFrame(()=>{const requested=state.force;state.queued=false;state.force=false;readOptions(requested);});
    }
    function onInput(event) {
      if(event.target.closest?.('.po-radioGroup-item')?.closest('.po-field-root')) scheduleRead(true);
    }
    const observer = new MutationObserver(mutations => {
      if(mutations.some(m => !gallery.contains(m.target) && !m.target.closest?.('product-sticky-form'))) scheduleRead();
    });
    observer.observe(scope,{childList:true,subtree:true,characterData:true,attributes:true,attributeFilter:['checked','aria-checked']});
    scope.addEventListener('change',onInput);
    scope.addEventListener('input',onInput);
    scope.addEventListener('click',onInput);
    const stickyRequest = () => syncSticky(gallery.dataset.sennaSelectedLivery || 'greatness');
    const sliderReady = () => scheduleRead();
    scope.addEventListener('senna:sticky-media-request',stickyRequest);
    gallery.addEventListener('slider:previousStatus',sliderReady);
    controllers.set(gallery,()=>{
      state.disposed=true;observer.disconnect();scope.removeEventListener('change',onInput);scope.removeEventListener('input',onInput);scope.removeEventListener('click',onInput);scope.removeEventListener('senna:sticky-media-request',stickyRequest);gallery.removeEventListener('slider:previousStatus',sliderReady);
    });
    Promise.all(['media-gallery','media-dots','slider-element'].map(name=>customElements.whenDefined(name))).then(()=>{
      if(state.disposed) return;
      state.ready=true;
      readOptions();
    });
    readOptions();
  }
  function initialize(root=document) {
    root.querySelectorAll('[data-senna-livery-gallery]').forEach(start);
  }
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',()=>initialize(),{once:true});
  else initialize();
  document.addEventListener('shopify:section:load',event=>initialize(event.target));
  document.addEventListener('shopify:section:unload',event=>{
    for(const [gallery,dispose] of controllers) if(event.target.contains(gallery)){dispose();controllers.delete(gallery);}
  });
  window.addEventListener('pageshow',()=>initialize());
})();
